import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import apis from '../../../../modules/apis'
import { axiosGet, axiosPost } from '../../../../modules/axiosHelper'
import {
  n_lesson_review,
  n_review,
  n_review_lessons,
  s_lesson_review,
} from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { COLORS, STUDY_REVIEW } from '../../../../modules/vars'
import LoadingSpinner from '../../../common/LoadingSpinner'

import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_check_filled, QIcon_check_outline, QIcon_student_custom } from '@repo/icons/index'
import { FEEDBACK_STATUS } from '../../../../model/feedback.interface'
import { useFeedbackStatusV5 } from '../../../../modules/apis/student/lesson'
import { useCurrentUser } from '../../../../modules/context/UserContext'

import { tracker } from '../../../review/useTracking'
import Divider from './Divider'

import { useEventTracking } from '../../../../helpers/gtmHelper'
import { useLessonInfo } from '../../../../modules/apis/student/lesson/review/queries'
import { IScheduleResult } from '../../../../modules/apis/student/lesson/schedule/queries'
import InduceSchedulePopup from '../../../InduceSchedulePopup'
import FeedbackLegacy from './FeedbackLegacy'
import MessageToStudent from './MessageToStudent'
import ReportLessonInfo from './ReportLessonInfo'
import WhatYouCanImprove from './WhatYouCanImprove'
import WhatYouDidGreat from './WhatYouDidGreat'

const Feedback = ({
  lessonId,
  setResult,
}: {
  lessonId: number
  setResult?: Dispatch<SetStateAction<IScheduleResult>>
}) => {
  const { currentUser } = useCurrentUser()
  const { sendClickEvent } = useEventTracking()
  const reportRef = useRef(null)
  const commentRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [json, setJson] = useState(null)
  const { t } = useTranslation()
  const { data: lesson } = useLessonInfo(lessonId)

  const feedbackStudy = lesson?.review_study?.list.find((item, i) => item.type == STUDY_REVIEW.FEEDBACK) || null
  const [feedbackCheck, setFeedbackCheck] = useState(feedbackStudy?.value || false)
  const [compliments, setCompliments] = useState<{
    compliments: any[]
    overall_compliment: string
  }>({
    compliments: [],
    overall_compliment: '',
  })
  const { data: status } = useFeedbackStatusV5()

  // 피드백 리포트 확인완료
  const onClickStudy = (e) => {
    e.preventDefault()
    sendClickEvent({ button_name: 'lesson_review_tutor_feedback_complete' })
    if (feedbackCheck) return

    if (lesson) {
      // score 수정
      const body = {
        score: 100,
        comment: commentRef?.current?.value,
      }
      axiosPost(apis.lessons.review.eval(lesson.id), body).then((res) => {
        if (res.success) {
          setFeedbackCheck(true)
          toast(t(s_lesson_review.completeFeedback))
        } else {
          alert('fail')
        }
      })
    }
  }

  const NOT_READY_PLACEHOLDER_MAP = {
    [FEEDBACK_STATUS.NOSHOW]: t(n_review.noshow_tutor), //노쇼
    [FEEDBACK_STATUS.IN_PROGRESS]: t(n_lesson_review.feedback_status_title1), //준비중
    [FEEDBACK_STATUS.WAIVER]: t(n_lesson_review.feedback_status_title2), // 리포트 제공 X
    [FEEDBACK_STATUS.WRITING_MATERIAL]: t(n_lesson_review.feedback_status_title2), // 라이팅 중심 수업
  }

  useEffect(() => {
    tracker('feedback')
    return () => tracker('feedback', true)
  }, [])

  useEffect(() => {
    if (!lessonId) return
    if (status === 'ready') {
      axiosGet(apis.lessons.review.feedbackV5(lessonId))
        .then((res) => {
          setJson(res)
        })
        .finally(() => setLoading(false))
      axiosGet(apis.lessons.review.feedbackComplimentsV5(lessonId)).then((res) => {
        setCompliments(res)
      })
    }
  }, [lessonId, status])

  useEffect(() => {
    setFeedbackCheck(lesson?.review_study?.list.find((item, i) => item.type == STUDY_REVIEW.FEEDBACK)?.value)
  }, [lesson])

  if (!lesson) return null
  // 3.0이전 수업
  const isBefore3point0 = json?.feedback_version !== '3.0'
  const feedbackCompleted = json?.feedback_status === 3
  const isFeedbackWaiver = json?.feedback_waiver ? json?.feedback_waiver.confirmed : false
  const isReadyFeedback = feedbackCompleted && !isFeedbackWaiver
  const isFeedbackPreparing = json?.feedback_status !== 3
  const isNoShow = json?.is_no_show
  const isNoFeedbackReportLesson = isNoShow || isFeedbackWaiver
  const isWritingFocusedLesson = !!json?.is_writing
  const studentName = currentUser?.profile?.name
  const tutorName = json?.tutor?.name
  const whatYouCanImproveList = json?.feedanswers
  const categories = []
  let categories_text = ''
  json?.feedanswers?.forEach((feedanswer) => {
    if (feedanswer?.title && !categories.includes(feedanswer?.title)) {
      if (categories_text != '') {
        categories_text += ' and '
      }
      categories.push(feedanswer.title)
      categories_text += `<span class="text-decoration-underline">${feedanswer.title}</span>`
    }
  })

  if (status !== FEEDBACK_STATUS.READY) {
    return (
      <div className="mx-auto w-full px-0 sm:w-[640px] lg:w-[800px] xl:w-[1040px] ">
        <div className="justify-content flex w-full flex-col items-center bg-white py-[120px] px-[20px] sm:px-[0px]">
          <QIcon_student_custom width={64} height={64} />
          <div className="mt-[20px] flex flex-col items-center text-center text-[18px] font-bold sm:text-[20px]">
            {NOT_READY_PLACEHOLDER_MAP[status] || t(n_lesson_review.feedback_status_title2)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {loading ? (
        <div className="mt-[30px] flex w-full items-center justify-center px-0 sm:w-[640px] lg:w-[800px] xl:w-[1040px]">
          <LoadingSpinner show primary padded />
        </div>
      ) : !isReadyFeedback ? (
        <div className="mx-auto w-full px-0  sm:w-[640px] lg:w-[800px] xl:w-[1040px] ">
          <div className="justify-content flex w-full flex-col items-center bg-white py-[120px]">
            <QIcon_student_custom width={64} height={64} />
            <div className="mt-[20px] flex flex-col items-center text-[20px] font-bold">
              {isNoFeedbackReportLesson
                ? t(n_lesson_review.feedback_status_title2)
                : isWritingFocusedLesson
                ? t(n_lesson_review.feedback_status_title2)
                : isFeedbackPreparing
                ? t(n_lesson_review.feedback_status_title1)
                : isNoShow
                ? t(n_review.noshow_tutor)
                : t(n_lesson_review.feedback_status_title2)}
            </div>
          </div>
        </div>
      ) : isBefore3point0 ? (
        <FeedbackLegacy lessonId={lessonId} isTeens={false} />
      ) : (
        <div className="mx-auto w-full px-0 sm:w-[640px] lg:w-[800px] xl:w-[1040px] ">
          <div ref={reportRef}>
            <ReportLessonInfo lesson={lesson} reportRef={reportRef}></ReportLessonInfo>
            <Divider className="mt-[16px]" />
            <MessageToStudent studentName={studentName} message={json.overall_comment} />
            <WhatYouDidGreat studentName={studentName} compliments={compliments} />
            <WhatYouCanImprove list={whatYouCanImproveList} pronunciation={json?.pronunciation} tutorName={tutorName} />
            <div className="mt-[40px] flex w-full flex-row items-center justify-center">
              <div>
                <DSButton
                  size={BUTTON_SIZE.LG}
                  type={BUTTON_TYPE.OUTLINE}
                  customBorderColor={COLORS.GRAY300}
                  onClick={onClickStudy}
                >
                  <span className="mr-[8px]">
                    {feedbackCheck ? (
                      <QIcon_check_filled color={COLORS.GREEN500} />
                    ) : (
                      <QIcon_check_outline color={COLORS.GRAY300} />
                    )}
                  </span>
                  <DSButton.Label>{t(n_review_lessons.review_completed_feedback)}</DSButton.Label>
                </DSButton>
              </div>
            </div>
          </div>
          <div className="h-[200px]" />
        </div>
      )}
      <InduceSchedulePopup setResult={setResult} time={30} />
    </div>
  )
}

export default Feedback
